import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Stepper, Step, StepLabel,
  Card, CardContent, CardActionArea, Grid, Chip,
  Divider, CircularProgress, Alert, Stack,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import LockIcon from "@mui/icons-material/Lock";
import EquipmentLeaseDialog from "./EquipmentLeaseDialog";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

// ─── Real packages from the Equipment Lease & Services Agreement ─────────
const PACKAGES = [
  {
    id: "igloo_single_entry",
    name: "Single Entry — Igloo",
    lockBrand: "Igloo",
    entryType: "Single Entry / Condo",
    monthlyRate: 11.95,
    setupFee: 0,
    termMonths: 48,
    description:
      "Smart access for a single entry point. Ideal for condos and single-door properties.",
    features: [
      "1 Igloo Smart Lock",
      "Remote access via app",
      "Access code management",
      "Showing automation",
      "Maintenance access automation",
      "Labor warranty",
      "90-day satisfaction guarantee",
      "Benefit Savings Membership",
    ],
    badge: "Most Affordable",
  },
  {
    id: "schlage_multi_entry",
    name: "Multi-Entry — Schlage Connected",
    lockBrand: "Schlage",
    entryType: "Multi-Entry",
    monthlyRate: 27.95,
    setupFee: 0,
    termMonths: 48,
    description:
      "Full smart access for multi-entry properties. Includes dedicated cellular connectivity — no Wi-Fi needed.",
    features: [
      "Schlage Connected Smart Lock(s)",
      "Dedicated cellular connectivity (no Wi-Fi required)",
      "Remote access via app",
      "Unlimited access codes",
      "Showing automation",
      "Maintenance access automation",
      "Tenant portal access",
      "Labor warranty",
      "Warranty support",
      "90-day satisfaction guarantee",
      "Benefit Savings Membership",
    ],
    badge: "Most Popular",
  },
];

const STEPS = ["Select Package", "Sign Agreement", "Order Created"];

const NewSmartPackageFlow = ({ open, onClose, uid, email, fullName }) => {
  const [activeStep, setActiveStep]       = useState(0);
  const [selectedPackage, setSelected]    = useState(null);
  const [leaseOpen, setLeaseOpen]         = useState(false);
  const [orderId, setOrderId]             = useState(null);
  const [creating, setCreating]           = useState(false);
  const [createError, setCreateError]     = useState(null);

  const handleClose = () => {
    if (creating) return;
    setActiveStep(0);
    setSelected(null);
    setLeaseOpen(false);
    setOrderId(null);
    setCreateError(null);
    onClose?.();
  };

  const handlePackageConfirm = () => {
    if (!selectedPackage) return;
    const newOrderRef = doc(collection(db, "smartPackageOrders"));
    setOrderId(newOrderRef.id);
    setActiveStep(1);
    setLeaseOpen(true);
  };

  const handleLeaseAgreed = async () => {
    setLeaseOpen(false);
    setCreating(true);
    setCreateError(null);
    try { 
      const orderRef = doc(collection(db, "smartPackageOrders"), orderId);
      await setDoc(orderRef, {
        uid,
        email,
        packageId:       selectedPackage.id,
        packageName:     selectedPackage.name,
        lockBrand:       selectedPackage.lockBrand,
        entryType:       selectedPackage.entryType,
        monthlyRate:     selectedPackage.monthlyRate,
        termMonths:      selectedPackage.termMonths,
        leaseOrderId:    orderId,  // ties back to agreements/{uid}/equipmentLeases/{orderId}
        status:          "submitted",
        assignedTechId:  null,
        createdAt:       serverTimestamp(),
      });
      setActiveStep(2);
    } catch (err) {
      console.error("[NewSmartPackageFlow] order creation failed:", err);
      setCreateError("Order could not be saved. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Dialog
        open={open && !leaseOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DevicesOtherOutlinedIcon color="primary" />
            <span>New Smart Package Order</span>
          </Stack>
        </DialogTitle>

        <Divider />

        <Box px={3} pt={2}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent sx={{ pt: 2 }}>

          {/* ── Step 0: Package Selection ── */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Choose the smart access package for your property. All packages include
                a <strong>48-month lease term</strong> and a{" "}
                <strong>90-day satisfaction guarantee</strong>.
              </Typography>
              <Grid container spacing={2}>
                {PACKAGES.map((pkg) => (
                  <Grid item xs={12} key={pkg.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderColor: selectedPackage?.id === pkg.id ? "primary.main" : "divider",
                        borderWidth: selectedPackage?.id === pkg.id ? 2 : 1,
                        transition: "border-color 0.15s",
                      }}
                    >
                      <CardActionArea onClick={() => setSelected(pkg)} sx={{ p: 0 }}>
                        <CardContent>
                          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                            <LockIcon color="primary" fontSize="small" />
                            <Typography fontWeight={700}>{pkg.name}</Typography>
                            {pkg.badge && (
                              <Chip
                                label={pkg.badge}
                                size="small"
                                color="primary"
                                sx={{ ml: "auto", fontSize: "0.7rem" }}
                              />
                            )}
                          </Stack>

                          <Typography variant="body2" color="text.secondary" mb={1}>
                            {pkg.description}
                          </Typography>

                          <Stack direction="row" spacing={3} mb={1.5}>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Monthly</Typography>
                              <Typography fontWeight={700} color="primary.main" fontSize="1.1rem">
                                ${pkg.monthlyRate.toFixed(2)}/mo
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Term</Typography>
                              <Typography fontWeight={600}>{pkg.termMonths} months</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Total</Typography>
                              <Typography fontWeight={600}>
                                ${(pkg.monthlyRate * pkg.termMonths).toFixed(2)}
                              </Typography>
                            </Box>
                          </Stack>

                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {pkg.features.map((f) => (
                              <Typography key={f} component="li" variant="body2" color="text.secondary">
                                {f}
                              </Typography>
                            ))}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                <Typography variant="caption">
                  <strong>Benefit Savings Membership</strong> — included with all packages.
                  Access member discounts on home improvement, restaurants, retail, travel,
                  and more through Benefit Hub.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* ── Step 1: Waiting / re-open lease ── */}
          {activeStep === 1 && !leaseOpen && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
              <GavelIcon sx={{ fontSize: 48, color: "primary.main" }} />
              <Typography variant="h6" fontWeight={700}>Equipment Lease Agreement</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Please complete the equipment lease agreement to continue.
              </Typography>
              {createError && <Alert severity="error" sx={{ width: "100%" }}>{createError}</Alert>}
              {creating ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">Creating your order…</Typography>
                </Stack>
              ) : (
                <Button variant="outlined" onClick={() => setLeaseOpen(true)}>
                  Re-open Agreement
                </Button>
              )}
            </Box>
          )}

          {/* ── Step 2: Order Confirmed ── */}
          {activeStep === 2 && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "success.main" }} />
              <Typography variant="h6" fontWeight={700}>Order Created!</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Your <strong>{selectedPackage?.name}</strong> order has been submitted at{" "}
                <strong>${selectedPackage?.monthlyRate.toFixed(2)}/mo</strong> for{" "}
                <strong>{selectedPackage?.termMonths} months</strong>.
                The installation team will be in touch shortly to schedule your setup.
              </Typography>
              {orderId && (
                <Box sx={{ bgcolor: "action.hover", borderRadius: 1.5, px: 2, py: 1, width: "100%", textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">Order ID</Typography>
                  <Typography variant="body2" fontWeight={600} fontFamily="monospace">{orderId}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          {activeStep === 0 && (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                disabled={!selectedPackage}
                onClick={handlePackageConfirm}
                startIcon={<InventoryOutlinedIcon />}
              >
                Continue to Agreement
              </Button>
            </>
          )}
          {activeStep === 2 && (
            <Button variant="contained" onClick={handleClose} fullWidth>Done</Button>
          )}
        </DialogActions>
      </Dialog>

      <EquipmentLeaseDialog
        open={leaseOpen}
        onClose={() => setLeaseOpen(false)}
        onAgreed={handleLeaseAgreed}
        uid={uid}
        email={email}
        fullName={fullName}
        selectedPackage={selectedPackage}
        orderId={orderId}
      />
    </>
  );
};

export default NewSmartPackageFlow;